import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";

/**
 * Single Fantasia ghost letter — a giant letter that floats up from the
 * staff, growing and dissolving into ink grains.
 *
 * Two things are hard here:
 *   1. SVG SMIL `<animate>` on `feDisplacementMap.scale` /
 *      `feTurbulence.baseFrequency` doesn't fire reliably for filters
 *      mounted dynamically inside React, so both are driven from a
 *      requestAnimationFrame loop.
 *   2. Framer-motion's keyframe-array opacity doesn't merge with a
 *      top-level duration in this version, so the fade is a plain CSS
 *      `gf-fade` keyframe (defined in App.css).
 */
export function GhostLetter({ ghost: g }) {
  const durMs = g.dur;
  const durS = durMs / 1000;
  const dispRef = useRef(null);
  const turbRef = useRef(null);

  useEffect(() => {
    const start = performance.now();
    let raf = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / durMs);
      const scale = 4 + (200 - 4) * (t * t);
      const freq = 0.015 + (0.32 - 0.015) * t;
      if (dispRef.current) dispRef.current.setAttribute("scale", scale.toFixed(2));
      if (turbRef.current) turbRef.current.setAttribute("baseFrequency", freq.toFixed(4));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [durMs]);

  const filterId = `gf-${g.id}`;

  return (
    <motion.div
      className="fixed pointer-events-none"
      initial={{ scale: 0.4, x: 0, y: 0, rotate: 0 }}
      animate={{ scale: 2.4, x: `${g.dx}vw`, y: `${g.dy}vh`, rotate: g.rotate }}
      transition={{
        scale: { duration: durS, ease: "linear" },
        x: { duration: durS, ease: "easeOut" },
        y: { duration: durS, ease: "easeOut" },
        rotate: { duration: durS, ease: "linear" },
      }}
      style={{
        // Bottom-anchored: the box's BOTTOM edge sits at cy (just above
        // the staff), so the letter never overlaps the notation.
        left: g.cx - g.box / 2,
        top: g.cy - g.box,
        width: g.box,
        height: g.box,
        willChange: "transform, opacity",
        filter: `drop-shadow(0 0 40px ${g.color}55) drop-shadow(0 0 80px ${g.color}33)`,
        animation: `gf-fade ${durS}s linear forwards`,
      }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 300 300" width="100%" height="100%" style={{ overflow: "visible" }}>
        <defs>
          <filter id={filterId} x="-60%" y="-60%" width="220%" height="220%">
            <feTurbulence
              ref={turbRef}
              type="fractalNoise"
              baseFrequency="0.015"
              numOctaves="2"
              seed={g.seed}
              result="noise"
            />
            <feDisplacementMap ref={dispRef} in="SourceGraphic" in2="noise" scale="4" />
          </filter>
        </defs>
        <text
          x="150"
          y="220"
          textAnchor="middle"
          fill={g.color}
          style={{
            fontFamily: "'Frank Ruhl Libre', 'Cormorant Garamond', serif",
            fontWeight: 500,
            fontSize: "220px",
          }}
          filter={`url(#${filterId})`}
        >
          {g.letter}
        </text>
      </svg>
    </motion.div>
  );
}

export default GhostLetter;
